// React-related imports
import { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Contexts and Hooks
import { MappingContext } from '../../../contexts/MappingContext';
import { GeneralizationContext } from '../../../contexts/GeneralizationContext';
import useSearchResult from '../../../hooks/useSearchResult';
import { useTextSelection } from '../../../hooks/generalizationHooks/useTextSelection';
import { useHistory } from '../../../hooks/useHistory';
import { useGeneralizationNavigation } from '../../../hooks/generalizationHooks/useGeneralizationNavigation';
import useAsyncOperation from '../../../hooks/useAsyncOperation';
import useClientInfo from '../../../hooks/useClientInfo';
import { useWebSocket } from '../../../hooks/useWebSocket';

// Helpers
import {
  generateRandomColor,
  deepCopyDocument,
  getAllNodesIdsInElement,
  getCatalogId,
  getCatalogWithSourceIdsFromAIResponse,
  getElement,
  getSuggestionByTargetNodeId,
  getCellElement,
  getUpdatedAiResponse,
  highlightSuggestion,
  updateAllSpanBackgroundColors,
  newCatalogNodes,
  extractNodeIdsFromAIResponse,
  removeHighlightSuggestions,
  getNodeIdAndValues,
  highlightMatchingElements,
  filteredAiResponse,
  getAllCells,
  setTargetElement,
  getCellNodeId,
  handleSpanNodeSelection,
  handleSelectionSearch,
  handleTextSelection,
  getFilteredCells,
  getMergedEditsIdsWithAiResponseIds,
  convertAIResponseToSuggestions,
  getDeletedNodes,
  getColorForAcceptedSuggestion,
  getColorForAiSuggestion,
  applyColorForNewCells,
  getNodeIdFromAttribute,
  hasPendingStructuralSave,
} from '../../../helpers/generalizationHelpers';
import {
  autoAcceptSuggestions,
  undoSuggestion,
  saveSuggestionsWithNodes,
  fetchMappingSessionComments,
} from '../../../services/apiCalls';
import { notifyError, notifySuccess } from '../../../helpers/utils';
import {
  applyCustomSVGs,
  applySVGToElement,
  getActiveSvgPin,
  handleSvgPinActivation,
  removeActivePin,
  removeAllSvgPins,
} from '../../../helpers/imageTaggingHelper';
import {
  copyAiResponseForDuplicatedTable,
  duplicateTableInEditor,
  DuplicateTableUndoContext,
  getTableForTargetNodeId,
  getTargetEditorWindowScroll,
  pruneAiResponseForMissingNodes,
  resolveUndoneDuplicateTableContext,
  restoreTargetViewAfterDuplicateTableUndo,
} from '../../../helpers/tableDuplicateHelpers';
import {
  buildAiResponseForTransferredTable,
  buildAuthorTransferSuggestions,
  getTableForSourceNodeId,
  getTargetTableInsertionHost,
  transferSourceTableInTargetEditor,
  TableTransferSuggestionPayload,
} from '../../../helpers/tableTransferHelpers';
import {
  excludeStyleAttribute,
  getHeaderOfCurrentCell,
  getUniqueIdentifierValue,
  getUpdatedCells,
  isUnnecessaryContent,
  removeSingleBrChild,
} from '../../../helpers/documentComparisonHelpers';

// Constants
import { AUTHORING_COLORS, mappingByType, sessionMapperType } from '../../../constants';
import { AddSourcesPreviousAction } from '../../catalog/util/constants';

// Types
import {
  AcceptanceMethodEnum,
  AIResponse,
  EditorRefObject,
  AISuggestionEntryValue,
  GeneralizationSuggestion,
  IdChangesMap,
  MappingTypeEnum,
  MarkerAttributes,
  NewSuggestion,
  SearchOptions,
} from '../../../types';

import _, { isEmpty, omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Editor as TinyMCEEditor } from 'tinymce';
import { useImageTagging } from './useImageTagging';
import { initialImageTagValues } from '../utils/constants';
import { AuthoringTargetSaveState, TargetSaveState } from './types';
import { useGeneralizationComments } from '../../../hooks/useGeneralizationComments';
import { useMappingSessionAPI } from '../../../services/api';
import sanitizeHtml from 'sanitize-html';
import { SmartSelectedTextMap, SmartSelectedTextRange } from '../../../components/SmartEditor';
import type { MappingSessionStore } from '../../../stores';
import { useFeatureFlag } from '../../../contexts';
import { FeatureFlag } from '../../../stores';

const AUTOSAVE_DELAY = 10000;

type HTMLElementWithLockHandlers = HTMLElement & {
  preventDefaultEvents?: (e: Event) => boolean;
};

type ZoomableStyle = CSSStyleDeclaration & { zoom?: string };

type SaveSuggestionPayload = Record<string, unknown> & {
  targetNodeId: string;
};

type UseGeneralizationAuthoringOptions = {
  mappingSessionStore?: MappingSessionStore;
};

export const useGeneralizationAuthoring = (options: UseGeneralizationAuthoringOptions = {}) => {
  const { mappingSessionStore } = options;
  const { isEnabled } = useFeatureFlag();
  const {
    editor1Ref,
    setEditor1Content,
    editor2Ref,
    sourceFileDoc,
    setMappingComments,
    addCommentBubble,
    mappingComments,
  } = useContext(MappingContext);

  const {
    loadingGeneratedDocument,
    acceptedSuggestions,
    aiResponse,
    generatedDocument,
    selectedCatalogId,
    loadingSourceDocument,
    sessionId,
    handleSourcechange,
    getSourceNodeIndex,
    setAcceptedSuggestions,
    setLoadingSourceDocument,
    setLastSaved,
    setSearchTerm,
    selectionSearch,
    aiResponseCopy,
    clearAISuggestions,
    mappingType,
    selectedValue,
    cells,
    setCells,
    setGeneratedDocumentWithAcceptedSuggestions,
    setLockSourceFile,
    lockSourceFile,
    targetNodeIdSuggestionMap,
    sourceNodeIdSuggestionMap,
    isAutoSave,
    initialTarget,
    setInitialTarget,
    selectedSourceDoc,
    baseTargetFileId,
  } = useContext(GeneralizationContext);
  const { autoFillSuggestions } = useMappingSessionAPI();
  const { userId, username, firstName, lastName } = useClientInfo();
  const fullName = `${firstName} ${lastName}`;

  const params = useParams();
  const navigate = useNavigate();

  const { addHistory, undo, redo, isUndoAvailable, isRedoAvailable, setHistory } = useHistory<
    GeneralizationSuggestion | NewSuggestion
  >([], `${params.id}_history`);

  const [isNewSuggestionsActive, setIsNewSuggestionsActive] = useState(false);
  const [updatedAiResponse, setUpdatedAiResponse] = useState<AIResponse>({});
  const [lockedAiResponse, setLockedAiResponse] = useState<AIResponse>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDisable, setIsDisable] = useState<boolean>(false);
  const [isDisabledApprove, setIsDisabledApprove] = useState<boolean>(true);
  const [suggestionIndex, setSuggestionIndex] = useState<{ value: number; nodeId?: string }>({
    value: 0,
    nodeId: '',
  });
  const [isAutofill, setIsAutofill] = useState(false);
  const [currentTargetNodeIds, setCurrentTargetNodeIds] = useState<string[]>([]);
  const [isImageNodeSelected, setIsImageNodeSelected] = useState(false);
  const [currentSourceNodeIds, setCurrentSourceNodeIds] = useState<string[]>([]);
  const [previousTargetNodeIds, setPreviousTargetNodeIds] = useState<string[]>([]);
  const [previousSourceNodeIds, setPreviousSourceNodeIds] = useState<string[]>([]);
  const [suggestionNodeValues, setSuggestionNodeValues] = useState<Array<[string, HTMLElement]>>(
    [],
  );
  const [aiSuggestionList, setAiSuggestionList] = useState<Array<Record<string, unknown>>>([]);
  const [isCtrlPressed, setIsCtrlPressed] = useState<boolean>(false);
  const [colors, setColors] = useState<string[]>([]);
  const { isSearched, setIsSearched, searchResult, setSearchResult } = useSearchResult();
  const [isSnappingEnabled, setIsSnapEnabled] = useState(false);
  const [copiedAIResponse, setCopiedAIResponse] = useState<AIResponse>({});
  const [isHeaderCellSelected, setIsHeaderCellSelected] = useState(false);
  const [targetChangesStatus, setTargetChangesStatus] = useState<AuthoringTargetSaveState>(
    TargetSaveState.UNCHANGED,
  );
  const [lastChangeTime, setLastChangeTime] = useState<number | null>(null);
  const [currentUserLockedNodes, setCurrentUserLockedNodes] = useState<Set<string>>(new Set());

  const autosaveTimerRef = useRef<NodeJS.Timeout>();
  const [isUndoEnabled, setIsUndoEnabled] = useState(false);
  const lastDuplicateOldToNewMapRef = useRef<Record<string, string> | null>(null);
  const duplicateUndoContextStackRef = useRef<DuplicateTableUndoContext[]>([]);
  const duplicateUndoContextPoppedRef = useRef<DuplicateTableUndoContext[]>([]);
  const duplicateTableUndoListenersAttachedRef = useRef(false);
  const queuedTableTransferSuggestionsRef = useRef<TableTransferSuggestionPayload[]>([]);
  const lastSourceTableAnchorRef = useRef<string | null>(null);
  const updatedAiResponseRef = useRef(updatedAiResponse);
  useEffect(() => {
    updatedAiResponseRef.current = updatedAiResponse;
  }, [updatedAiResponse]);
  const activeTimerRef = useRef<NodeJS.Timeout>();
  const currentUserLockedNodesRef = useRef<Set<string>>(new Set());
  const isUpdatingClasses = useRef(false);
  const userPickedSourceRef = useRef(false);

  const setGeneratedDocRef = useRef(setGeneratedDocumentWithAcceptedSuggestions);
  useEffect(() => {
    setGeneratedDocRef.current = setGeneratedDocumentWithAcceptedSuggestions;
  }, [setGeneratedDocumentWithAcceptedSuggestions]);

  const { lockedNodes, lockNode, unlockNode, isLockedByOtherUser, getNodeLocker, activeUsers } =
    useWebSocket<GeneralizationSuggestion>({
      sessionId: sessionId || null,
      userId,
      username,
      fullName,
      onUpdateMessage: useCallback(
        (newSuggestions: GeneralizationSuggestion[]) => {
          setAcceptedSuggestions((prevSuggestions = []) => {
            const nonConflictingSuggestions = prevSuggestions.filter(
              (suggestion: GeneralizationSuggestion) =>
                !newSuggestions.some(
                  (newSug: GeneralizationSuggestion) =>
                    newSug.targetNodeId === suggestion.targetNodeId,
                ),
            );

            const newAcceptedSuggestions = [...nonConflictingSuggestions, ...newSuggestions];
            const currentDocument = editor1Ref?.current?.getDoc();
            if (currentDocument && newAcceptedSuggestions.length) {
              setGeneratedDocRef.current(currentDocument, false, newSuggestions);
            }

            return newAcceptedSuggestions;
          });
        },
        [editor1Ref, setAcceptedSuggestions],
      ),
    });

  useEffect(() => {
    currentUserLockedNodesRef.current = currentUserLockedNodes;
  }, [currentUserLockedNodes]);

  useEffect(() => {
    return () => {
      // Clear any active timers
      if (activeTimerRef.current) {
        clearTimeout(activeTimerRef.current);
        activeTimerRef.current = undefined;
      }
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = undefined;
      }

      // Unlock any nodes that were locked by current user
      if (currentUserLockedNodesRef.current.size > 0) {
        currentUserLockedNodesRef.current.forEach((nodeId) => {
          unlockNode(nodeId);
        });
        currentUserLockedNodesRef.current = new Set();
      }
    };
  }, [unlockNode]);

  const handleSnappingToggle = () => {
    setIsSnapEnabled((prevState) => !prevState);
  };

  const nodeIdsFromAIResponse = useMemo(
    () => extractNodeIdsFromAIResponse(updatedAiResponse, editor1Ref),
    [updatedAiResponse, editor1Ref],
  );

  const filteredCells = getFilteredCells(
    cells,
    mappingType,
    currentTargetNodeIds,
    editor1Ref,
    nodeIdsFromAIResponse,
  );

  const newAiResponse = useMemo(() => {
    if (!aiResponse || !acceptedSuggestions) return {};
    return getUpdatedAiResponse(aiResponse, acceptedSuggestions) as AIResponse;
  }, [aiResponse, acceptedSuggestions]);

  const {
    isNextCellAvailable: isNextAuthoringCellAvailable,
    isPreviousCellAvailable: isPreviousAuthoringCellAvailable,
    navigateToCell: navigateToAuthoringCell,
    updateIndex,
  } = useGeneralizationNavigation(
    filteredCells,
    -1,
    getCellNodeId(currentTargetNodeIds[0], editor1Ref),
  );

  const {
    templateSelectionOnClickHandler,
    sourceSelectionOnClickHandler,
    updateTextSelectionNode,
    linkTextSelectionNode,
    sourceSelection,
    targetSelection,
    resetAllActiveTextHighlights,
    resetSourceTextHighlight,
  } = useTextSelection();

  const handleLockedFileChange = () => {
    setLockSourceFile((prev) => !prev);
  };

  const autoFillHandler = async () => {
    setIsAutofill(true);
    if (sessionId) {
      const result = await autoFillSuggestions.mutateAsync({ id: sessionId });
      const tempSuggestions = [...(acceptedSuggestions || []), ...result.data.data];
      setAcceptedSuggestions(tempSuggestions);
      tempSuggestions.forEach((s) => {
        if (s) setTargetElement(editor1Ref, s.targetValue, s.targetNodeId);
      });
      setIsAutofill(false);
      const doc = editor1Ref.current?.getDoc();
      if (doc) setInitialTarget(deepCopyDocument(doc));
    }
  };

  const sourceNodeId = useCallback((): string[] | undefined => {
    const currentTemplateNodeSuggestions =
      acceptedSuggestions &&
      acceptedSuggestions.filter(
        (suggestion) => suggestion.targetNodeId === currentTargetNodeIds[0],
      );
    if (currentTemplateNodeSuggestions && currentTemplateNodeSuggestions.length > 1)
      return currentTemplateNodeSuggestions.map((suggestion) => suggestion.sourceNodeId);
    else {
      const sourceNodeIds = _.keys(
        _.get(_.get(updatedAiResponse, `${currentTargetNodeIds[0]}`), `[${suggestionIndex.value}]`),
      );
      return sourceNodeIds.length ? [sourceNodeIds[sourceNodeIds.length - 1]] : [];
    }
  }, [acceptedSuggestions, currentTargetNodeIds, suggestionIndex.value, updatedAiResponse]);

  const sourceFileId = useCallback((): number | undefined => {
    return _.get(
      _.get(
        _.values(
          _.get(
            _.get(updatedAiResponse, `${currentTargetNodeIds[0]}`),
            `[${suggestionIndex.value}]`,
          ),
        ),
        '[0]',
      ),
      'sourceFileId',
    );
  }, [currentTargetNodeIds, suggestionIndex.value, updatedAiResponse]);

  const getSuggestionColor = useCallback(
    (nodeId: string) => {
      const element = getElement(editor1Ref, nodeId);
      const nodeIds = getAllNodesIdsInElement(element);
      const acceptedSuggestion = acceptedSuggestions?.find((suggestion) =>
        nodeIds.includes(suggestion.targetNodeId),
      );

      if (acceptedSuggestion) {
        return getColorForAcceptedSuggestion(acceptedSuggestion.mapperType || '');
      } else if (aiResponse && nodeIds.some((id) => id in aiResponse)) {
        return getColorForAiSuggestion(mappingType);
      }
    },
    [editor1Ref, acceptedSuggestions, aiResponse, mappingType],
  );

  const highlightSuggestions = useCallback(
    (
      editorRef: EditorRefObject,
      nodeIds: string[],
      color: string | null = null,
      isScroll = false,
    ) => {
      nodeIds?.forEach((nodeId) => {
        const element = getElement(editorRef, nodeId);
        if (element) {
          const highlightColor = color || getSuggestionColor(nodeId);
          highlightSuggestion({
            element,
            color: highlightColor,
            isScroll,
          });
        }
      });
    },
    [getSuggestionColor],
  );

  const getCurrentNodeId = useCallback(
    (
      id: string,
      editorRef: EditorRefObject = editor1Ref,
      aiResponseData: AIResponse = updatedAiResponse,
    ) => {
      const element = getElement(editorRef, id);
      if (!element) return '';

      const cellElement = getCellElement(element, editor1Ref.current?.getBody() as Node);
      const allNodesIds = getAllNodesIdsInElement(cellElement ?? element);
      const nodeWithSuggestion = allNodesIds.find((nodeId) => !!aiResponseData[nodeId]);
      return nodeWithSuggestion || cellElement?.getAttribute('data-nodeid') || id;
    },
    [editor1Ref, updatedAiResponse],
  );

  const handleSourceNodeIds = useCallback((nodeIds: string[] = []) => {
    setCurrentSourceNodeIds((prev) => {
      setPreviousSourceNodeIds([...prev]);
      return nodeIds;
    });
  }, []);

  const handleTargetNodeIds = useCallback((nodeIds: string[] = []) => {
    setCurrentTargetNodeIds((prevTargetNodeIds) => {
      setPreviousTargetNodeIds(prevTargetNodeIds);
      return nodeIds;
    });
  }, []);

  const handleTargetNodeIdsRef = useRef(handleTargetNodeIds);
  useEffect(() => {
    handleTargetNodeIdsRef.current = handleTargetNodeIds;
  }, [handleTargetNodeIds]);

  const setColorsRef = useRef(setColors);
  useEffect(() => {
    setColorsRef.current = setColors;
  }, [setColors]);

  const handleReset = () => {
    handleTargetNodeIds();
    handleSourceNodeIds();
    resetAllActiveTextHighlights();
    if (initialTarget && editor1Ref.current?.isDirty()) {
      setGeneratedDocumentWithAcceptedSuggestions(initialTarget);
    }
  };

  const handleNodeLock = useCallback(
    (nodeId: string) => {
      lockNode(nodeId);
      setCurrentUserLockedNodes((prev) => new Set([...prev, nodeId]));
    },
    [lockNode],
  );

  const targetOnClickHandler = (event: MouseEvent, editor: TinyMCEEditor) => {
    const target = event.target as HTMLElement;
    const selectedCells = editor.model.table.getSelectedCells();
    const selectedText = editor.selection.getContent({ format: 'text' }).trim();
    const hasPartialTextSelection =
      selectedCells.length <= 1 && !editor.selection.isCollapsed() && selectedText.length > 0;

    const editor2Body = editor2Ref.current?.getBody();
    if (!editor2Body) return;

    const sourceIds = sourceNodeId();
    const updateSourceNodeIds = !!sourceIds && !_.isEqual(sourceIds, currentSourceNodeIds);

    if (hasPartialTextSelection) {
      handleSelectionSearch(target, selectionSearch, setSearchTerm);
      const textSelectionNode = templateSelectionOnClickHandler(target);
      const removeTextHighlight = handleTextSelection(
        currentTargetNodeIds,
        editor1Ref,
        (id: string, editorRef?: EditorRefObject) =>
          getCurrentNodeId(id, editorRef ?? editor1Ref, updatedAiResponse),
        handleSourceNodeIds,
        handleTargetNodeIds,
        selectedCells,
        sourceIds || [],
        updateSourceNodeIds,
        textSelectionNode,
      );
      if (removeTextHighlight) resetAllActiveTextHighlights();
      return;
    }

    const spans = editor2Body.querySelectorAll('span[data-selectionid][data-sourceid]');

    updateAllSpanBackgroundColors(spans as NodeListOf<HTMLElement>);
    handleSelectionSearch(target, selectionSearch, setSearchTerm);
    handleSpanNodeSelection(
      target,
      sourceNodeIdSuggestionMap,
      selectedCatalogId,
      handleSourcechange,
      editor1Ref,
      editor2Ref,
      currentTargetNodeIds,
    );

    const textSelectionNode =
      selectedCells.length > 1 ? null : templateSelectionOnClickHandler(target);
    const removeTextHighlight = handleTextSelection(
      currentTargetNodeIds,
      editor1Ref,
      (id: string, editorRef?: EditorRefObject) =>
        getCurrentNodeId(id, editorRef ?? editor1Ref, updatedAiResponse),
      handleSourceNodeIds,
      handleTargetNodeIds,
      selectedCells,
      sourceIds || [],
      updateSourceNodeIds,
      textSelectionNode,
    );

    if (removeTextHighlight) resetAllActiveTextHighlights();
    else return;

    setIsSearched(false);

    const cellElement = getCellElement(target, editor1Ref.current?.getBody() as Node);
    const cellNodeId = cellElement?.getAttribute('data-nodeid');
    const nodeId = target.getAttribute('data-nodeid');

    if (selectedCells.length > 1) {
      // Handle multiple table cells as target nodes before single-node click logic.
      setIsImageNodeSelected(false);
      setIsDisabledApprove(_.isEmpty(selectedCells));

      const nodeIds = selectedCells
        .map((cell: HTMLElement) => getCurrentNodeId(cell.getAttribute('data-nodeid') || ''))
        .filter((id: string) => !isLockedByOtherUser(id));

      if (_.isEqual(currentTargetNodeIds, nodeIds)) {
        return;
      }

      setCurrentTargetNodeIds((prev: string[]) => {
        setPreviousTargetNodeIds(prev);
        const newIds: string[] = nodeIds.filter((id: string) => !prev.includes(id));

        if (isCtrlPressed) {
          // Track nodes when locking
          newIds.forEach((nodeId) => {
            handleNodeLock(nodeId);
          });
          const newColors = [...prev, ...newIds].map((id: string, index: number) =>
            generateRandomColor(colors.length + index),
          );
          setColors((prev) => [...prev, ...newColors.splice(prev.length)]);
          return [...prev, ...newIds];
        } else {
          // Track nodes when locking
          nodeIds.forEach((nodeId: string) => {
            handleNodeLock(nodeId);
          });
          const newColors = nodeIds.map((id: string, index: number) => generateRandomColor(index));
          handleSourceNodeIds();
          setColors(newColors);
          return [...nodeIds];
        }
      });
      selectedCells.forEach((cell: HTMLElement) => {
        editor.dom.setAttrib(cell, 'data-mce-selected', null);
      });
      editor.selection.collapse();
      return;
    }

    // If node is locked by another user, take over the lock on explicit click
    if (cellNodeId && isLockedByOtherUser(cellNodeId)) {
      const lockerInfo = getNodeLocker(cellNodeId);
      notifyError(`Taking over node from ${lockerInfo?.username}`);
    }

    // Handle single table cell as target node
    if (nodeId) {
      setIsImageNodeSelected(false);
      // Handle single Ctrl key press
      if (isCtrlPressed) {
        setCurrentTargetNodeIds((prev: string[]) => {
          setPreviousTargetNodeIds(prev);
          if (prev.includes(nodeId)) {
            return prev.filter((id: string) => id !== nodeId);
          } else {
            const cellNodeId = getCellNodeId(getCurrentNodeId(nodeId), editor1Ref);
            cellNodeId && updateIndex(cellNodeId);
            // Track the node when locking
            handleNodeLock(getCurrentNodeId(nodeId));
            return [...prev, getCurrentNodeId(nodeId)];
          }
        });
        const isAlreadyExists = currentTargetNodeIds.includes(nodeId);
        if (isAlreadyExists) {
          const nodeIndex = currentTargetNodeIds.indexOf(nodeId);
          setColors((prevColors) => {
            const updatedColors = [...prevColors];
            updatedColors.splice(nodeIndex, 1);
            return updatedColors;
          });
          setCurrentSourceNodeIds((prevSourceNodeIds) => {
            setPreviousSourceNodeIds(prevSourceNodeIds);
            const updatedSourceNodeIds = [...prevSourceNodeIds];
            updatedSourceNodeIds.splice(nodeIndex, 1);
            return updatedSourceNodeIds;
          });
        } else {
          setColors((prevColors) => [
            ...prevColors,
            prevColors.length === 0
              ? AUTHORING_COLORS.SELECTED
              : generateRandomColor(prevColors.length),
          ]);
        }
      } else {
        const currentNodeId = nodeId;

        // Track the node when locking
        handleNodeLock(currentNodeId);

        const referenceSelectionSuggestion = targetNodeIdSuggestionMap[currentNodeId];
        if (isNewSuggestionsActive && !currentTargetNodeIds.includes(currentNodeId)) {
          setCurrentTargetNodeIds((prev: string[]) => {
            setPreviousTargetNodeIds(prev);
            if (prev.length === 1 && !referenceSelectionSuggestion?.sourceFileId) return prev;
            return [...prev, currentNodeId];
          });

          if (currentTargetNodeIds.length === 1) {
            if (!referenceSelectionSuggestion?.sourceFileId) {
              notifyError('Invalid selection. Please try again.');
            } else if (referenceSelectionSuggestion?.sourceFileId) {
              navigate('/add-source', {
                state: {
                  sessionId,
                  previousAction: AddSourcesPreviousAction.DYNAMIC_MAPPING,
                  dynamicMappingPayload: {
                    targetSelectionNodeId: currentTargetNodeIds.at(0),
                    referenceSelectionNodeId: currentNodeId,
                    referenceSelectionSuggestionId: referenceSelectionSuggestion.id,
                  },
                },
              });
            }
          }
          return;
        }

        handleSourceNodeIds();
        setColors([AUTHORING_COLORS.SELECTED]);
        handleTargetNodeIds([nodeId]);
        const cellNodeId = getCellNodeId(nodeId, editor1Ref);
        cellNodeId && updateIndex(cellNodeId);
      }
    } else if (!_.isEmpty(selectedCells)) {
      // Handle multiple table cell as target node
      setIsImageNodeSelected(false);
      setIsDisabledApprove(_.isEmpty(selectedCells));

      const nodeIds = selectedCells
        .map((cell: HTMLElement) => getCurrentNodeId(cell.getAttribute('data-nodeid') || ''))
        .filter((id: string) => !isLockedByOtherUser(id));

      if (_.isEqual(currentTargetNodeIds, nodeIds)) {
        return;
      }

      setCurrentTargetNodeIds((prev: string[]) => {
        setPreviousTargetNodeIds(prev);
        const newIds: string[] = nodeIds.filter((id: string) => !prev.includes(id));

        if (isCtrlPressed) {
          // Track nodes when locking
          newIds.forEach((nodeId) => {
            handleNodeLock(nodeId);
          });
          const newColors = [...prev, ...newIds].map((id: string, index: number) =>
            generateRandomColor(colors.length + index),
          );
          setColors((prev) => [...prev, ...newColors.splice(prev.length)]);
          return [...prev, ...newIds];
        } else {
          // Track nodes when locking
          nodeIds.forEach((nodeId: string) => {
            handleNodeLock(nodeId);
          });
          const newColors = nodeIds.map((id: string, index: number) => generateRandomColor(index));
          handleSourceNodeIds();
          setColors(newColors);
          return [...nodeIds];
        }
      });
      selectedCells.forEach((cell: HTMLElement) => {
        editor.dom.setAttrib(cell, 'data-mce-selected', null);
      });
    } else {
      const nodeId = target.getAttribute('data-nodeid');
      setColors([AUTHORING_COLORS.SELECTED]);
      if (nodeId && target.nodeName !== 'BODY') {
        // Track the node when locking
        handleNodeLock(nodeId);
        handleTargetNodeIds([nodeId]);
        handleSourceNodeIds();
      }

      if (nodeId && target.nodeName === 'IMG') {
        setIsImageNodeSelected(true);
        return;
      }

      setIsImageNodeSelected(false);
    }

    editor.selection.collapse();
  };
  const handleSourceImageNodeSelection = (sourceNode: HTMLElement, targetNode: HTMLElement) => {
    if (!sourceNode) return;

    const isImageToImageSelection =
      currentTargetNodeIds.length && targetNode?.tagName === 'IMG' && sourceNode.nodeName === 'IMG';

    const nodeIdToUse = isImageToImageSelection
      ? getNodeIdFromAttribute(sourceNode)
      : getNodeIdFromAttribute(sourceNode.parentElement);

    handleSourceNodeIds([nodeIdToUse]);
  };

  const applyLockedNodeClasses = useCallback(
    (element: HTMLElement, username: string) => {
      const nodeId = element.getAttribute('data-nodeid');
      const isCurrentUser = nodeId && lockedNodes[nodeId] && lockedNodes[nodeId].userId === userId;

      if (isCurrentUser && element.classList.contains('locked-node')) {
        // Current user took over this lock — remove lock styles left by previous owner
        removeLockedNodeClasses(element);
        return;
      }

      if (!isCurrentUser && !element.classList.contains('locked-node')) {
        // Store original background color before changing it
        const originalBackgroundColor = element.style.backgroundColor;
        element.setAttribute('data-original-bg-color', originalBackgroundColor);

        element.style.backgroundColor = 'rgba(128, 128, 128, 0.4)';
        element.style.cursor = 'not-allowed';

        element.classList.add('locked-node');
        element.setAttribute('title', `Locked by ${username}`);
        element.setAttribute('contenteditable', 'false');

        const allChildren = element.querySelectorAll('*');
        allChildren.forEach((child) => {
          (child as HTMLElement).setAttribute('contenteditable', 'false');
          (child as HTMLElement).style.cursor = 'not-allowed';
        });
      }
    },
    [lockedNodes, userId],
  );

  const removeLockedNodeClasses = (element: HTMLElement) => {
    if (element.classList.contains('locked-node')) {
      element.classList.remove('locked-node');
      element.removeAttribute('title');
      element.removeAttribute('contenteditable');
      element.removeAttribute('data-has-lock-listeners');

      // Restore original background color if it was stored
      const originalBgColor = element.getAttribute('data-original-bg-color');
      if (originalBgColor !== null) {
        element.style.backgroundColor = originalBgColor;
        element.removeAttribute('data-original-bg-color');
      } else {
        element.style.backgroundColor = '';
      }

      element.style.cursor = '';

      const allChildren = element.querySelectorAll('*');
      allChildren.forEach((child) => {
        (child as HTMLElement).removeAttribute('contenteditable');
        (child as HTMLElement).style.cursor = '';
      });

      const lockableElement = element as HTMLElementWithLockHandlers;
      if (lockableElement.preventDefaultEvents) {
        const preventDefaultEvents = lockableElement.preventDefaultEvents;

        const events = ['dblclick', 'click', 'mousedown', 'mouseup', 'contextmenu'];
        events.forEach((eventType) => {
          element.removeEventListener(eventType, preventDefaultEvents, true);
        });

        delete lockableElement.preventDefaultEvents;
      }
    }
  };

  useEffect(() => {
    if (!editor1Ref?.current || !editor1Ref.current.getBody()) return;
    isUpdatingClasses.current = true;
    try {
      // First, remove all existing locked-node classes
      const existingLockedNodes = editor1Ref.current.getBody().querySelectorAll('.locked-node');
      existingLockedNodes.forEach((node) => {
        const htmlNode = node as HTMLElement;
        const nodeId = htmlNode.getAttribute('data-nodeid');
        if (nodeId && !Object.keys(lockedNodes).includes(nodeId)) {
          removeLockedNodeClasses(htmlNode);
        }
      });
      // Apply locked styles to currently locked nodes
      Object.entries(lockedNodes).forEach(([nodeId, lockInfo]) => {
        if (nodeId) {
          const element = getElement(editor1Ref, nodeId);
          if (element) {
            applyLockedNodeClasses(element, lockInfo.username);
          }
        }
      });
    } finally {
      isUpdatingClasses.current = false;
    }
  }, [lockedNodes, applyLockedNodeClasses, editor1Ref]);

  const sourceDocumentOnClickHandler = (event: MouseEvent, editor: TinyMCEEditor) => {
    const target = event.target as HTMLElement;
    if (!target.parentElement || !currentTargetNodeIds.length) return;
    const sourceNodeId = target.parentElement.getAttribute('data-nodeid');

    if (
      currentTargetNodeIds.length === 1 &&
      isImageTagging &&
      target.nodeName === 'IMG' &&
      sourceNodeId &&
      event.offsetX &&
      event.offsetY
    ) {
      removeActivePin(editor.getDoc());

      const parentWidth = target.parentElement.offsetWidth;
      const parentHeight = target.parentElement.offsetHeight;

      const zoomLevel = parseFloat(
        (getComputedStyle(target.ownerDocument.body) as ZoomableStyle).zoom || '1',
      );

      const offsetXInPercent = ((event.offsetX / zoomLevel) * 100) / parentWidth;
      const offsetYInPercent = ((event.offsetY / zoomLevel) * 100) / parentHeight;

      applySVGToElement(
        {
          positionX: offsetXInPercent,
          positionY: offsetYInPercent,
          targetId: currentTargetNodeIds[0],
        },
        sourceNodeId,
        editor2Ref,
        'active',
      );
      openTaggingSlideOver();
    }

    const selectedCells = editor.model.table.getSelectedCells();
    const selectedText = editor.selection.getContent({ format: 'text' }).trim();
    const hasPartialTextSelection =
      selectedCells.length <= 1 && !editor.selection.isCollapsed() && selectedText.length > 0;

    if (hasPartialTextSelection) {
      if (targetSelection) {
        const textSelectionNode = sourceSelectionOnClickHandler(target);
        if (textSelectionNode && !_.isEqual(currentTargetNodeIds, [textSelectionNode])) {
          handleSourceNodeIds([textSelectionNode]);
        }
      }
      return;
    }

    const textSelectionNode =
      selectedCells.length > 1 ? null : sourceSelectionOnClickHandler(target);
    if (textSelectionNode) {
      if (!_.isEqual(currentTargetNodeIds, [textSelectionNode]))
        handleSourceNodeIds([textSelectionNode]);
      return;
    }

    resetSourceTextHighlight();
    const targetNode = getElement(editor1Ref, currentTargetNodeIds[0]);
    if (target.nodeName === 'IMG' && targetNode) {
      handleSourceImageNodeSelection(target, targetNode);
      return;
    }

    const editor2Body = editor2Ref.current?.getBody();
    if (!editor2Body) return;

    const cellElement = getCellElement(target, editor2Body);
    const clickedNodeId =
      target.getAttribute('data-nodeid') || cellElement?.getAttribute('data-nodeid') || '';
    const isTdElementWithDataNodeId = cellElement && cellElement.hasAttribute('data-nodeid');

    selectedCells.forEach((cell: HTMLElement) => {
      editor.dom.setAttrib(cell, 'data-mce-selected', null);
    });

    userPickedSourceRef.current = true;
    if (selectedCells.length > 1) setIsDisabledApprove(false);
    setCurrentSourceNodeIds((prev) => {
      setPreviousSourceNodeIds(prev);
      if (isCtrlPressed && isTdElementWithDataNodeId) {
        if (prev.includes(clickedNodeId)) {
          const index = prev.indexOf(clickedNodeId);
          setColors((prevColors) => {
            const updatedColors = [...prevColors];
            if (prev.length === prevColors.length - 1) {
              updatedColors.splice(index, 1);
            }
            return updatedColors;
          });
          return prev.filter((nodeId) => nodeId !== clickedNodeId);
        } else {
          if (prev.length + 1 <= colors.length) {
            return [...prev, clickedNodeId];
          }
        }
      } else if (isCtrlPressed) {
        const newSelectedNodeIds = selectedCells.map(
          (cell: HTMLElement) => cell.getAttribute('data-nodeid') || '',
        );

        const filteredNodeIds = newSelectedNodeIds.filter(
          (nodeId: string) => !prev.includes(nodeId),
        );

        if (prev.length + filteredNodeIds.length <= colors.length) {
          return [...prev, ...filteredNodeIds];
        }
      } else if (selectedCells.length > 1) {
        const newSelectedNodeIds = selectedCells.map(
          (cell: HTMLElement) =>
            cell.getAttribute('data-nodeid') || cellElement?.getAttribute('data-nodeid') || '',
        );
        if (newSelectedNodeIds.length <= colors.length) {
          return newSelectedNodeIds;
        } else {
          return newSelectedNodeIds.slice(0, colors.length);
        }
      } else if (isTdElementWithDataNodeId) {
        return [clickedNodeId];
      }
      return target.hasAttribute('data-nodeid') ? [target.getAttribute('data-nodeid') ?? ''] : prev;
    });

    editor.selection.collapse();
  };

  const getCellElementIds = useCallback(
    (aiResponseData: AIResponse) => {
      return Object.keys(aiResponseData).map((id) => {
        const el = getElement(editor1Ref, id);
        if (el) {
          return el?.getAttribute('data-nodeid') || '';
        }
        return '';
      });
    },
    [editor1Ref],
  );

  const addCommentBubbles = useCallback(() => {
    mappingComments.forEach((comment) =>
      addCommentBubble(comment.mappingNumber, comment.targetNodeId),
    );
  }, [mappingComments, addCommentBubble]);

  const suggestionsWithColors = useCallback(() => {
    if (_.keys(updatedAiResponse)?.length > 0) {
      const nodeIds: string[] = getCellElementIds(updatedAiResponse);
      return nodeIds?.map((nodeId) => ({
        targetNodeId: nodeId,
        color: getSuggestionColor(nodeId),
      }));
    }
  }, [updatedAiResponse, getCellElementIds, getSuggestionColor]);

  const highlightPreviousNode = useCallback(
    (editorRef: EditorRefObject, isSourceDocument = false, previousElement: HTMLElement | null) => {
      const previousElementNodeIds: string[] = getAllNodesIdsInElement(previousElement);
      highlightSuggestions(
        editorRef,
        previousElementNodeIds,
        isSourceDocument ? '#D3D3D3' : null,
        false,
      );
    },
    [highlightSuggestions],
  );

  const highlightCurrentNode = useCallback(
    (
      editorRef: EditorRefObject,
      defaultColor: string = AUTHORING_COLORS.SELECTED,
      currentElement: HTMLElement | null,
      isScroll = true,
    ) => {
      if (!currentElement) {
        return;
      }

      const elementNodeIds: string[] = getAllNodesIdsInElement(currentElement);
      const hasPartialText =
        targetSelection || currentElement.querySelector('span[data-highlight="active"]');

      if (hasPartialText) {
        handleSvgPinActivation(editor2Ref, currentTargetNodeIds.at(0));
        return;
      }

      highlightSuggestions(editorRef, elementNodeIds, defaultColor, isScroll);
      handleSvgPinActivation(editor2Ref, currentTargetNodeIds.at(0));
    },
    [currentTargetNodeIds, editor2Ref, highlightSuggestions, targetSelection],
  );

  const highlightCurrentNodes = useCallback(() => {
    currentTargetNodeIds.forEach((currentTargetNodeId, index) => {
      const element = getElement(editor1Ref, currentTargetNodeId);
      if (!element) return;
      highlightCurrentNode(editor1Ref, colors[index], element, false);
    });
  }, [colors, currentTargetNodeIds, editor1Ref, highlightCurrentNode]);

  const highlightAllTargetSuggestions = useCallback(
    (editorRef: EditorRefObject) => {
      const nodeIds: string[] = getCellElementIds(updatedAiResponse);
      const nodeValues = getNodeIdAndValues(nodeIds, editor1Ref);
      setSuggestionNodeValues(nodeValues);
      const mergedNodeIds = getMergedEditsIdsWithAiResponseIds(
        nodeIds,
        acceptedSuggestions,
        editor1Ref,
      );
      highlightSuggestions(editorRef, mergedNodeIds, null);
      highlightCurrentNodes();
      addCommentBubbles();
    },
    [
      acceptedSuggestions,
      addCommentBubbles,
      editor1Ref,
      getCellElementIds,
      highlightCurrentNodes,
      highlightSuggestions,
      updatedAiResponse,
    ],
  );

  const autofillSuggestions = () => {
    setLoadingSourceDocument(true);
    autoAcceptSuggestions(sessionId)
      .then((res) => {
        setAcceptedSuggestions((prevAcceptedSuggestions) => [
          ...[prevAcceptedSuggestions || []],
          ...res.data,
        ]);
        window.location.reload();
      })
      .catch((err) => {
        notifyError(err.message);
        console.error(err);
      })
      .finally(() => {
        setLoadingSourceDocument(false);
      });
  };

  const removeMergedCellsIfExists = (updatedDoc?: Document) => {
    const editor1Doc = updatedDoc?.body;
    if (!editor1Doc) {
      return;
    }

    const mergedCells = editor1Doc.querySelectorAll<HTMLElement>('[data-merged-cell]');

    mergedCells.forEach((cell) => {
      const nodeId = cell.getAttribute('data-nodeId');
      if (nodeId) {
        cell.removeAttribute('data-merged-cell');
      }
    });
  };

  const callSaveSuggestions = async (
    currentSuggestions: SaveSuggestionPayload[],
    acceptanceMethodProp: AcceptanceMethodEnum,
    addToHistory = true,
    setCells = true,
  ) => {
    if (!generatedDocument || !generatedDocument?.id) return;
    const generatedDocumentId = generatedDocument.id;
    const newCatNodes = newCatalogNodes(editor1Ref, generatedDocument) ?? [];
    const deletedNodes = getDeletedNodes(initialTarget, editor1Ref?.current?.getDoc());
    const queuedTransferSuggestions = queuedTableTransferSuggestionsRef.current;
    if (
      !currentSuggestions.length &&
      !newCatNodes.length &&
      !deletedNodes.length &&
      !Object.keys(copiedAIResponse).length &&
      !queuedTransferSuggestions.length
    ) {
      return;
    }

    removeMergedCellsIfExists(editor1Ref?.current?.getDoc());

    const suggestionsToSave = [
      ...currentSuggestions,
      ...queuedTransferSuggestions,
      ...(copiedAIResponse
        ? convertAIResponseToSuggestions(copiedAIResponse, editor1Ref, sessionId)
        : []),
    ];

    const filteredSuggestionsToSave = suggestionsToSave.filter((suggestion) => {
      const targetValue = suggestion.targetValue;
      return typeof targetValue === 'string' ? !isUnnecessaryContent(targetValue) : true;
    });
    let res;
    try {
      if (newCatNodes.length) {
        applyColorForNewCells(
          editor1Ref,
          newCatNodes,
          filteredSuggestionsToSave as Parameters<typeof applyColorForNewCells>[2],
        );
      }
      res = await saveSuggestionsWithNodes({
        acceptanceMethod: acceptanceMethodProp,
        suggestions: filteredSuggestionsToSave,
        newCatalogNodes: {
          nodes: newCatNodes,
          catalogId: generatedDocumentId,
        },
        deletedNodes: { nodes: deletedNodes, catalogId: generatedDocumentId },
        sessionId,
      });
      setCopiedAIResponse({});
      queuedTableTransferSuggestionsRef.current = [];
    } catch (e) {
      notifyError('Error occurred in saving document');
      console.error(e);
    }

    if (!res?.data?.suggestions) {
      return;
    }

    const suggestions: GeneralizationSuggestion[] = res.data.suggestions;
    addToHistory && addHistory(suggestions);
    if (setCells) {
      setAllCells();
    }

    const savedTargetIds = new Set(
      filteredSuggestionsToSave.map((suggestion) => suggestion.targetNodeId),
    );

    const suggestionMap = suggestions.reduce((map, suggestion) => {
      if (suggestion.selected || savedTargetIds.has(suggestion.targetNodeId)) {
        map.set(suggestion.targetNodeId, suggestion);
      }
      return map;
    }, new Map<string, GeneralizationSuggestion>());

    setLastSaved(new Date(res.data.lastSavedDate));

    const mergedAcceptedSuggestions = acceptedSuggestions
      ? (() => {
          const next = [...acceptedSuggestions];
          suggestionMap.forEach((value, key) => {
            const existingIndex = next.findIndex((suggestion) => suggestion.targetNodeId === key);
            if (value.targetValue?.includes('data-highlight=') || existingIndex === -1) {
              next.push(value);
            } else {
              next[existingIndex] = {
                ...next[existingIndex],
                ...value,
              };
            }
          });
          return next;
        })()
      : suggestions;

    setAcceptedSuggestions(mergedAcceptedSuggestions);
    mappingSessionStore?.syncAcceptedSuggestionsFromSave(mergedAcceptedSuggestions);

    // Release locks on saved target nodes so other users can edit them
    savedTargetIds.forEach((nodeId) => {
      unlockNode(nodeId);
      setCurrentUserLockedNodes((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    });

    return suggestions;
  };

  const handleNewSmartSuggestion = async (
    targetElements: Element[],
    sourceElements: Element[],
    sourceFileId: string,
    acceptanceMethod: AcceptanceMethodEnum,
    suggestions?: Array<
      | GeneralizationSuggestion
      | NewSuggestion
      | (AISuggestionEntryValue & { sourceNodeId: string })
      | undefined
    >,
    selectedText?: SmartSelectedTextMap,
    selectedRange?: SmartSelectedTextRange,
  ) => {
    const restSourceData = omit(initialImageTagValues, 'sourceDocumentData');
    if (!Array.isArray(acceptedSuggestions) || !generatedDocument) return;
    let x: string | undefined, y: string | undefined;
    const targetNodeIds = targetElements.map((element) => {
      const nodeId = element.getAttribute('data-nodeid');
      if (!nodeId) {
        throw new Error('Element missing required data-nodeid attribute');
      }
      return nodeId;
    });
    const sourceNodeIds = sourceElements.map((element) => {
      const nodeId = element.getAttribute('data-nodeid');
      if (!nodeId) {
        throw new Error('Element missing required data-nodeid attribute');
      }
      return nodeId;
    });

    if (isEmpty(targetNodeIds)) return;

    const currentSuggestions = targetNodeIds.map(
      (currentTargetNodeId, index): NewSuggestion | GeneralizationSuggestion => {
        let targetValue;
        let sourceValue;
        const oldTargetValue = targetElements[index].innerHTML;

        const sourceDocumentElement = removeAllSvgPins(
          sourceElements[index] as HTMLElement,
          true,
        ) as HTMLElement;

        const mapperType: string =
          _.findIndex(aiResponse && aiResponse[currentTargetNodeId ?? ''], (o) =>
            _.has(o, sourceNodeIds[index]),
          ) !== -1
            ? sessionMapperType[mappingType]
            : mappingByType.author;

        if (!targetValue) {
          if (sourceDocumentElement?.tagName === 'IMG' && targetElements[index].tagName === 'IMG') {
            targetValue = sourceDocumentElement?.getAttribute('src');
          } else if (selectedText && selectedRange) {
            const sourceRange = selectedRange.source;
            const targetRange = selectedRange.target;
            const sourceLiveCell = sourceElements[index] as HTMLElement;
            const linkSourceNodeId =
              sourceLiveCell.getAttribute('data-nodeid') || sourceNodeIds[index] || '';
            const selectionId = uuidv4();

            const srcDoc = sourceLiveCell.ownerDocument;
            const srcWrapper = srcDoc.createElement('span');
            srcWrapper.setAttribute('data-sourceid', linkSourceNodeId);
            srcWrapper.setAttribute('data-selectionid', selectionId);
            srcWrapper.setAttribute('data-highlight', 'partial');

            const extractedSource = sourceRange.extractContents();
            while (extractedSource.firstChild) {
              srcWrapper.appendChild(extractedSource.firstChild);
            }
            sourceRange.insertNode(srcWrapper);

            sourceValue = srcWrapper.innerHTML.replace(/ style="[^"]*background[^"]*"/g, '');

            const tgtEl = targetElements[index] as HTMLElement;
            const tgtDoc = tgtEl.ownerDocument;
            const tgtWrapper = tgtDoc.createElement('span');
            tgtWrapper.setAttribute('data-sourceid', linkSourceNodeId);
            tgtWrapper.setAttribute('data-selectionid', selectionId);
            tgtWrapper.setAttribute('data-highlight', 'partial');

            const innerHolder = tgtDoc.createElement('div');
            innerHolder.innerHTML = sourceValue;
            while (innerHolder.firstChild) {
              tgtWrapper.appendChild(innerHolder.firstChild);
            }

            targetRange.deleteContents();
            targetRange.insertNode(tgtWrapper);

            targetValue = tgtEl.innerHTML;
          } else {
            targetValue =
              sourceDocumentElement?.innerHTML &&
              sourceDocumentElement.innerHTML.replace(/ style="[^"]*background[^"]*"/g, '');
          }

          if (targetElements[index].tagName === 'IMG') {
            const nodeIndex = _.findIndex(aiResponse && aiResponse[currentTargetNodeId], (o) =>
              _.has(o, sourceNodeIds[index]),
            );
            const imageNode =
              aiResponse?.[currentTargetNodeId]?.[nodeIndex]?.[sourceNodeIds[index]];
            if (isImageNodeSelected && imageNode?.suggestionType === 'image') {
              targetValue = imageNode?.value;
            }
          }
        }
        const suggestion = getSuggestionByTargetNodeId(acceptedSuggestions, currentTargetNodeId);
        return suggestion
          ? ({
              ...suggestion,
              sourceFile: { id: sourceFileId },
              sourceFileId: sourceFileId,
              sourceNodeId: sourceNodeIds[index],
              sessionId,
              documentId: sourceFileId,
              mapperType,
              targetValue,
              targetFileId: generatedDocument.id,
              previousSuggestionId: suggestion.id,
              sourceValue: targetElements[index]?.tagName === 'IMG' ? undefined : sourceValue,
              positionX: x ? parseFloat(x) : null,
              positionY: y ? parseFloat(y) : null,
              suggestionType: targetElements[index]?.tagName === 'IMG' ? 'image' : 'text',
              columnHeader: undefined,
              ...restSourceData,
            } as unknown as GeneralizationSuggestion)
          : ({
              id: null,
              sessionId: sessionId,
              targetNodeId: currentTargetNodeId,
              sourceNodeId: sourceNodeIds[index],
              documentId: sourceFileId,
              targetFileId: generatedDocument.id,
              targetValue,
              mapperType: mapperType,
              sourceValue: targetElements[index]?.tagName === 'IMG' ? undefined : sourceValue,
              sourceFileId: sourceFileId,
              oldTargetValue,
              positionX: x ? parseFloat(x) : null,
              positionY: y ? parseFloat(y) : null,
              suggestionType: targetElements[index]?.tagName === 'IMG' ? 'image' : 'text',
              columnHeader: '',
              ...restSourceData,
            } as NewSuggestion);
      },
    );

    currentSuggestions.forEach((suggestion) => {
      if (!targetNodeIdSuggestionMap[suggestion.targetNodeId]) {
        const previousValues = {
          sessionId: sessionId,
          targetNodeId: suggestion.targetNodeId,
          sourceNodeId: '',
          documentId: sourceFileId,
          targetValue: suggestion.oldTargetValue,
          mapperType: suggestion.mapperType,
          sourceValue: '',
          id: suggestion.id,
        } as NewSuggestion;
        addHistory([previousValues]);
      }
      setTargetElement(editor1Ref, suggestion.targetValue, suggestion.targetNodeId);
    });

    try {
      const saveResult = await callSaveSuggestions(
        currentSuggestions,
        acceptanceMethod,
        false,
        false,
      );
      setTargetChangesStatus(TargetSaveState.AUTOSAVED);

      if (saveResult && mappingSessionStore) {
        mappingSessionStore.addSourceNodeIds(sourceNodeIds);
        mappingSessionStore.updateBaselineAfterSave('target');
      }

      if (saveResult && editor1Ref.current) {
        setInitialTarget(deepCopyDocument(editor1Ref.current.getDoc()));
        setLastChangeTime(null);
        if (autosaveTimerRef.current) {
          clearTimeout(autosaveTimerRef.current);
          autosaveTimerRef.current = undefined;
        }
        const editor = editor1Ref.current as TinyMCEEditor;
        if (editor && !_.keys(compareDocuments()).length) {
          editor.isNotDirty = true;
          editor.save();
        }
        setIsUndoEnabled(false);
      }
    } catch (error) {
      console.error('Error updating suggestions:', error);
    }
  };

  const handleNewSuggestions = async (
    sourceData = initialImageTagValues,
    acceptanceMethod: AcceptanceMethodEnum,
  ) => {
    const { sourceDocumentData, ...restSourceData } = sourceData;
    if (!Array.isArray(acceptedSuggestions) || !generatedDocument) return;
    let x: string | undefined, y: string | undefined, svgElement: HTMLElement | null;

    const currentSuggestions = currentTargetNodeIds.map(
      (currentTargetNodeId, index): NewSuggestion | GeneralizationSuggestion => {
        let targetValue;
        let sourceValue;
        let oldTargetValue;
        let newDataNodeValue;
        const oldTargetElement = getElement(editor1Ref, currentTargetNodeId);

        const updatedSelection = updateTextSelectionNode();
        if (updatedSelection) {
          const {
            targetValue: targetVal,
            sourceValue: sourceVal,
            oldTargetNodeValue,
            newSpanDataNodeId,
          } = updatedSelection;
          targetValue = targetVal;
          sourceValue = sourceVal;
          oldTargetValue = oldTargetNodeValue;
          newDataNodeValue = newSpanDataNodeId;
        } else {
          oldTargetValue = oldTargetElement?.innerHTML;
        }
        let sourceDocumentElement = getElement(editor2Ref, currentSourceNodeIds[index]);
        if (sourceDocumentElement) {
          sourceDocumentElement = removeAllSvgPins(sourceDocumentElement, true) as HTMLElement;
        }

        const suggestion = getSuggestionByTargetNodeId(acceptedSuggestions, currentTargetNodeId);
        const mapperType: string =
          _.findIndex(aiResponse && aiResponse[currentTargetNodeId], (o) =>
            _.has(o, currentSourceNodeIds[index]),
          ) !== -1
            ? sessionMapperType[mappingType]
            : mappingByType.author;
        if (!targetValue) {
          if (sourceDocumentElement?.tagName === 'IMG' && oldTargetElement?.tagName === 'IMG') {
            targetValue = sourceDocumentElement?.getAttribute('src');
          } else {
            targetValue =
              sourceDocumentElement?.innerHTML &&
              sourceDocumentElement.innerHTML.replace(/ style="[^"]*background[^"]*"/g, '');
          }
          if (oldTargetElement?.tagName === 'IMG') {
            const nodeIndex = _.findIndex(aiResponse && aiResponse[currentTargetNodeId], (o) =>
              _.has(o, currentSourceNodeIds[index]),
            );
            const imageNode =
              aiResponse?.[currentTargetNodeId]?.[nodeIndex]?.[currentSourceNodeIds[index]];
            if (isImageNodeSelected && imageNode?.suggestionType === 'image') {
              targetValue = imageNode?.value;
            }
          }
        }

        if (currentTargetNodeIds.length === 1 && isImageTagging) {
          const sourceDoc = editor2Ref.current?.getDoc();
          svgElement = sourceDoc ? getActiveSvgPin(sourceDoc) : null;
          x = svgElement?.style.left;
          y = svgElement?.style.top;
          if (svgElement) targetValue = sourceDocumentData;
          setTargetElement(editor1Ref, sourceDocumentData, currentTargetNodeId);
        }
        const sourceFileId = getCatalogId(editor2Ref, currentSourceNodeIds[index]) || 0;

        let headerNameOfCurrentCell;

        let indentifierCellValue;

        if (selectedSourceDoc?.dataSource?.id) {
          const nodeid = sourceDocumentElement?.getAttribute('data-nodeid');
          const editor2Body = editor2Ref.current?.getBody();
          const tdElement = editor2Body?.querySelector(
            `[data-nodeid="${nodeid}"]`,
          ) as HTMLTableCellElement | null;
          const tableElement = tdElement?.closest('table') ?? null;
          const identifierCell = tdElement
            ? getUniqueIdentifierValue(tdElement, selectedSourceDoc?.dataSource?.uniqueIdentifier)
            : null;
          indentifierCellValue = identifierCell?.textContent?.trim();
          headerNameOfCurrentCell =
            tdElement && tableElement ? getHeaderOfCurrentCell(tdElement, tableElement) : undefined;
        }

        return suggestion
          ? ({
              ...suggestion,
              sourceFile: { id: sourceFileId },
              sourceFileId: sourceFileId,
              sourceNodeId: currentSourceNodeIds[index],
              sessionId,
              targetNodeId: newDataNodeValue ? newDataNodeValue : suggestion.targetNodeId,
              documentId: sourceFileId,
              mapperType,
              targetValue,
              targetFileId: generatedDocument.id,
              previousSuggestionId: suggestion.id,
              sourceValue: oldTargetElement?.tagName === 'IMG' ? undefined : sourceValue,
              positionX: x ? parseFloat(x) : null,
              positionY: y ? parseFloat(y) : null,
              suggestionType: oldTargetElement?.tagName === 'IMG' ? 'image' : 'text',
              columnHeader: headerNameOfCurrentCell,
              uniqueIdentifierValue: indentifierCellValue,
              ...restSourceData,
            } as GeneralizationSuggestion)
          : ({
              id: null,
              sessionId: sessionId,
              targetNodeId: newDataNodeValue ? newDataNodeValue : currentTargetNodeId,
              sourceNodeId: currentSourceNodeIds[index],
              documentId: sourceFileId,
              targetFileId: generatedDocument.id,
              targetValue,
              mapperType: mapperType,
              sourceValue: oldTargetElement?.tagName === 'IMG' ? undefined : sourceValue,
              sourceFileId: sourceFileId,
              oldTargetValue,
              positionX: x ? parseFloat(x) : null,
              positionY: y ? parseFloat(y) : null,
              suggestionType: oldTargetElement?.tagName === 'IMG' ? 'image' : 'text',
              columnHeader: headerNameOfCurrentCell,
              uniqueIdentifierValue: indentifierCellValue,
              ...restSourceData,
            } as NewSuggestion);
      },
    );
    currentSuggestions.forEach((suggestion) => {
      if (!targetNodeIdSuggestionMap[suggestion.targetNodeId]) {
        const previousValues = {
          sessionId: sessionId,
          targetNodeId: suggestion.targetNodeId,
          sourceNodeId: '',
          documentId: getCatalogId(editor2Ref, currentSourceNodeIds[0]),
          targetValue: suggestion.oldTargetValue,
          mapperType: suggestion.mapperType,
          sourceValue: '',
          id: suggestion.id,
        } as NewSuggestion;
        addHistory([previousValues]);
      }
      if (!svgElement) {
        setTargetElement(editor1Ref, suggestion.targetValue, suggestion.targetNodeId);
      }
    });

    try {
      await callSaveSuggestions(currentSuggestions, acceptanceMethod, false);
      const doc = editor1Ref.current?.getDoc();
      if (doc) setInitialTarget(deepCopyDocument(doc));
      setTargetChangesStatus(TargetSaveState.AUTOSAVED);
    } catch (error) {
      console.error('Error updating suggestions:', error);
    }
  };

  const { isLoading: isSavingSuggestions, executeCallback: receiveSuggestions } =
    useAsyncOperation(handleNewSuggestions);

  const {
    toggleImageTagging,
    isImageTagging,
    isTaggingSlideOverOpen,
    imageTagFormValues,
    openTaggingSlideOver,
    closeTaggingSlideOver,
    handleSubmitTag,
  } = useImageTagging({
    onSubmit: receiveSuggestions,
    selectedTargetNode: currentTargetNodeIds[0],
    updatedAiResponse,
  });

  const handleClosePinningSlideOver = () => {
    closeTaggingSlideOver();
    handleSourceNodeIds();
  };

  const handleGeneralizationAuthoringUndo = () => {
    const suggestion = undo();
    if (!suggestion) return;

    const { targetNodeId = '', targetValue = '' } = suggestion.previous || {};
    const { id } = suggestion.current;

    if (!acceptedSuggestions || !targetNodeId) return;
    const filteredAcceptedSuggestions = acceptedSuggestions.filter(
      (obj: GeneralizationSuggestion) => obj.id !== id,
    );
    if (suggestion.previous.id)
      filteredAcceptedSuggestions.push(suggestion.previous as GeneralizationSuggestion);
    const deletedSuggestion = acceptedSuggestions.find(
      (suggestion: GeneralizationSuggestion) => suggestion.id === id,
    );

    try {
      setTargetElement(editor1Ref, targetValue, targetNodeId);
      setAcceptedSuggestions(filteredAcceptedSuggestions);
      if (!currentTargetNodeIds.includes(targetNodeId)) {
        handleTargetNodeIds([targetNodeId]);
      }

      id &&
        undoSuggestion(id).then((res) => {
          setLastSaved(new Date(res.data.lastSavedDate));
        });
    } catch (error) {
      deletedSuggestion &&
        setTargetElement(editor1Ref, deletedSuggestion.targetValue, targetNodeId);
      setAcceptedSuggestions(acceptedSuggestions);
      console.error('Error deleting suggestions:', error);
    }
  };

  const handleGeneralizationAuthoringRedo = () => {
    const suggestion = redo();
    if (!suggestion) {
      return;
    }

    const { id, order, ...nextSuggestion } = suggestion.next as GeneralizationSuggestion;
    if (!acceptedSuggestions || !nextSuggestion.targetNodeId) return;

    try {
      setTargetElement(editor1Ref, nextSuggestion.targetValue, nextSuggestion.targetNodeId);

      if (!currentTargetNodeIds.includes(nextSuggestion.targetNodeId)) {
        handleTargetNodeIds([nextSuggestion.targetNodeId]);
      }

      id &&
        callSaveSuggestions(
          [
            {
              ...nextSuggestion,
              sessionId,
              documentId: nextSuggestion.sourceFileId,
              order: nextSuggestion.previousSuggestionId ? order : undefined,
            },
          ],
          AcceptanceMethodEnum.AUTHOR,
          false,
        ).then((suggestions) => {
          if (!suggestions?.length) return;
          setHistory((previousHistory) => {
            const newHistory = previousHistory.map((v) => {
              if (v.id === id) {
                v.id = suggestions[0].id;
              }

              if (v.previousSuggestionId === null) {
                v.order = suggestions[0].id;
              }

              if (v.previousSuggestionId === id) {
                v.previousSuggestionId = suggestions[0].id;
                v.order = suggestions[0].order;
              }

              return v;
            });
            return newHistory;
          });
        });
    } catch (error) {
      console.error('Error deleting suggestions:', error);
    }
  };

  const compareDocuments = useCallback((): IdChangesMap => {
    if (!initialTarget) return {};
    const originalDoc = deepCopyDocument(initialTarget);
    const updatedDocSource = editor1Ref.current?.getDoc();
    const updatedDoc = updatedDocSource ? deepCopyDocument(updatedDocSource) : undefined;

    const changes: IdChangesMap = {};
    const sanitizeContent = (html: string): string => {
      return sanitizeHtml(html, {
        allowedTags: false,
        allowedAttributes: false,
        allowVulnerableTags: true,
        disallowedTagsMode: 'recursiveEscape',
        textFilter: function (text: string) {
          return text.replace(/\s+/g, ' ').trim();
        },
      });
    };

    if (originalDoc && updatedDoc) {
      const updatedNodes = updatedDoc.querySelectorAll<HTMLElement>(
        '[data-nodeId]:not([data-comment])',
      );

      updatedNodes.forEach((updatedNode) => {
        const nodeId = updatedNode.getAttribute('data-nodeId') || '';
        let innerHTML = excludeStyleAttribute(updatedNode.innerHTML);
        innerHTML = sanitizeContent(innerHTML);
        const updatedInnerHtml = _.unescape(innerHTML);

        const originalNode = originalDoc.querySelector<HTMLElement>(`[data-nodeId='${nodeId}']`);

        let originalInnerHTML = '';
        if (originalNode) {
          originalInnerHTML = excludeStyleAttribute(originalNode.innerHTML);
          originalInnerHTML = sanitizeContent(originalInnerHTML);
        }

        const isEmptyNode = removeSingleBrChild(updatedNode).children.length === 0;

        if (
          isEmptyNode &&
          originalNode &&
          _.unescape(originalInnerHTML) !== _.unescape(innerHTML)
        ) {
          changes[nodeId] = {
            old: _.unescape(originalNode.innerHTML),
            updated: updatedInnerHtml,
          };
        } else {
          if (!originalNode) {
            const hasContent = sanitizeContent(updatedNode.textContent || '');
            if (hasContent) {
              changes[nodeId] = { old: '', updated: updatedInnerHtml };
            }
          } else {
            // Use textContent for comparison so text inside child elements (e.g. <p>) is detected.
            // removeChildNodeTextContent only returns direct text, missing wrapped content entirely.
            const oldNodeTextContent = sanitizeContent(originalNode.textContent || '');
            const updatedNodeTextContent = sanitizeContent(updatedNode.textContent || '');

            if (oldNodeTextContent !== updatedNodeTextContent) {
              if (originalNode.childElementCount !== updatedNode.childElementCount) {
                const copyNode = updatedNode.cloneNode(true) as HTMLElement;
                for (const child of Array.from(copyNode.children) as HTMLElement[]) {
                  child.removeAttribute('data-newnode');
                }
                changes[nodeId] = {
                  old: originalNode.innerHTML,
                  updated: copyNode.innerHTML,
                };
              } else {
                changes[nodeId] = {
                  old: _.unescape(originalInnerHTML),
                  updated: updatedInnerHtml,
                };
              }
            }
          }
        }
      });
    }
    return changes;
  }, [editor1Ref, initialTarget]);

  const handleSaveChanges = async (
    afterSaveTargetStatus: TargetSaveState = TargetSaveState.SAVED,
    acceptanceMethod: AcceptanceMethodEnum,
  ) => {
    if (!initialTarget || !editor1Ref?.current?.getDoc()) return;

    const originalDocument = deepCopyDocument(initialTarget);
    const updatedDocument = deepCopyDocument(editor1Ref?.current?.getDoc());
    let updatedCells = [];

    const documentChanges = originalDocument && updatedDocument ? compareDocuments() : {};
    if (sessionId && acceptedSuggestions) {
      updatedCells = getUpdatedCells(
        documentChanges,
        sessionId,
        editor1Ref,
        targetNodeIdSuggestionMap,
        updatedAiResponse,
      );
      const parser = new DOMParser();
      const doc = parser.parseFromString(initialTarget.body.outerHTML, 'text/html');

      updatedCells.forEach((cell) => {
        const element = doc.querySelector(`[data-nodeid='${cell.targetNodeId}']`) || {};
        const previousValues = {
          sessionId: sessionId,
          targetNodeId: cell.targetNodeId,
          sourceNodeId: '',
          documentId: cell.documentId,
          targetValue: (element as HTMLElement).innerHTML,
          mapperType: cell.mapperType,
          sourceValue: '',
          id: null,
        } as NewSuggestion;
        addHistory([previousValues]);
      });

      await callSaveSuggestions(updatedCells, acceptanceMethod, false);
      setTargetChangesStatus(afterSaveTargetStatus);
      const editorDoc = editor1Ref.current?.getDoc();
      if (editorDoc) setInitialTarget(deepCopyDocument(editorDoc));
      const editor = editor1Ref.current;
      if (editor && !_.keys(compareDocuments()).length) {
        editor.isNotDirty = true;
        editor.save();
        setIsUndoEnabled(false);
      }
    }
  };

  const { isLoading: isSaving, executeCallback: handleSave } = useAsyncOperation(handleSaveChanges);

  // Stable ref so autosave timer always calls the latest handleSave without being in its deps.
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  const runAutoSave = !(
    mappingType === MappingTypeEnum.AUTO_MAPPING ||
    isSaving ||
    isSavingSuggestions ||
    isAutoSave ||
    isAutofill
  );

  const lockCurrentNodes = useCallback(() => {
    currentTargetNodeIds.forEach((nodeId) => {
      lockNode(nodeId);
    });
  }, [currentTargetNodeIds, lockNode]);

  const lockCurrentNodesRef = useRef(lockCurrentNodes);
  useEffect(() => {
    lockCurrentNodesRef.current = lockCurrentNodes;
  }, [lockCurrentNodes]);

  const isDocumentChanged = useCallback(() => {
    const newNode = editor1Ref?.current?.getBody()?.querySelector('[data-newnode]');
    const deletedNodes = getDeletedNodes(initialTarget, editor1Ref?.current?.getDoc());
    return _.keys(compareDocuments()).length > 0 || newNode || deletedNodes.length;
  }, [editor1Ref, initialTarget, compareDocuments]);

  const handleEditorChange = useCallback(() => {
    if (!runAutoSave) return;

    // Check if current target node is locked by another user
    if (currentTargetNodeIds.length > 0) {
      const lockedNode = currentTargetNodeIds.find((nodeId) => isLockedByOtherUser(nodeId));

      if (lockedNode) {
        const lockerInfo = getNodeLocker(lockedNode);
        if (lockerInfo) {
          notifyError(`This node is being edited by ${lockerInfo.username}`);
          return;
        }
      }
    }

    // Update timestamp of last change
    setLastChangeTime(Date.now());
    setIsUndoEnabled(true);
  }, [runAutoSave, currentTargetNodeIds, isLockedByOtherUser, getNodeLocker]);

  const isDuplicateTableEnabled = useMemo(() => {
    if (!currentTargetNodeIds.length) {
      return false;
    }

    const anchorNodeId = currentTargetNodeIds[0];
    if (isLockedByOtherUser(anchorNodeId)) {
      return false;
    }

    if (
      isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) &&
      mappingSessionStore?.isLocked(anchorNodeId)
    ) {
      return false;
    }

    return !!getTableForTargetNodeId(editor1Ref, anchorNodeId);
  }, [currentTargetNodeIds, editor1Ref, isLockedByOtherUser, isEnabled, mappingSessionStore]);

  const hasPendingAuthoringSave = useCallback(
    (trackedSuggestions: SaveSuggestionPayload[] = []) => {
      if (trackedSuggestions.length > 0) {
        return true;
      }

      if (hasPendingStructuralSave(editor1Ref, initialTarget, generatedDocument)) {
        return true;
      }

      if (queuedTableTransferSuggestionsRef.current.length > 0) {
        return true;
      }

      return Object.keys(copiedAIResponse).length > 0;
    },
    [copiedAIResponse, editor1Ref, generatedDocument, initialTarget],
  );

  const attachDuplicateTableUndoListeners = useCallback(() => {
    const editor = editor1Ref.current;
    if (!editor || duplicateTableUndoListenersAttachedRef.current) {
      return;
    }
    duplicateTableUndoListenersAttachedRef.current = true;

    const syncAiResponseWithEditorDom = (isRedo: boolean) => {
      const body = editor.getBody();
      if (!body) {
        return;
      }

      if (!isRedo) {
        setUpdatedAiResponse((prev) => pruneAiResponseForMissingNodes(prev, body));
        setCopiedAIResponse((prev) => pruneAiResponseForMissingNodes(prev, body));
        queuedTableTransferSuggestionsRef.current =
          queuedTableTransferSuggestionsRef.current.filter(
            (suggestion) =>
              !!body.querySelector(`[data-nodeid="${CSS.escape(suggestion.targetNodeId)}"]`),
          );

        const undoContext = resolveUndoneDuplicateTableContext(
          body,
          duplicateUndoContextStackRef.current,
        );
        if (!undoContext) {
          return;
        }

        duplicateUndoContextStackRef.current = duplicateUndoContextStackRef.current.filter(
          (context) => context !== undoContext,
        );
        duplicateUndoContextPoppedRef.current.push(undoContext);

        window.setTimeout(() => {
          const restoredAnchorNodeId = restoreTargetViewAfterDuplicateTableUndo(
            editor,
            undoContext,
          );
          if (!restoredAnchorNodeId) {
            return;
          }

          userPickedSourceRef.current = true;
          handleTargetNodeIdsRef.current([restoredAnchorNodeId]);
          setColorsRef.current([AUTHORING_COLORS.SELECTED]);

          if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && mappingSessionStore) {
            mappingSessionStore.setActiveNode('target', restoredAnchorNodeId);
            mappingSessionStore.setActiveContent('target', editor.getContent());
          }
        }, 0);
        return;
      }

      const restoredContext = duplicateUndoContextPoppedRef.current.find((context) =>
        Object.values(context.oldToNewNodeIdMap).every(
          (nodeId) => !!body.querySelector(`[data-nodeid="${CSS.escape(nodeId)}"]`),
        ),
      );
      if (!restoredContext) {
        return;
      }

      duplicateUndoContextPoppedRef.current = duplicateUndoContextPoppedRef.current.filter(
        (context) => context !== restoredContext,
      );
      duplicateUndoContextStackRef.current.push(restoredContext);
      lastDuplicateOldToNewMapRef.current = restoredContext.oldToNewNodeIdMap;

      const newNodeIds = Object.values(restoredContext.oldToNewNodeIdMap);

      const currentResponse = updatedAiResponseRef.current;
      const needsRestore = newNodeIds.some((nodeId) => !currentResponse[nodeId]);
      if (!needsRestore) {
        return;
      }

      const restoredSuggestions = copyAiResponseForDuplicatedTable(
        restoredContext.oldToNewNodeIdMap,
        currentResponse,
        (nodeId) => getCurrentNodeId(nodeId, editor1Ref, currentResponse),
      );

      if (Object.keys(restoredSuggestions).length) {
        setUpdatedAiResponse((prev) => ({ ...prev, ...restoredSuggestions }));
        setCopiedAIResponse((prev) => ({ ...prev, ...restoredSuggestions }));
      }
    };

    editor.on('Undo', () => syncAiResponseWithEditorDom(false));
    editor.on('Redo', () => syncAiResponseWithEditorDom(true));
  }, [editor1Ref, getCurrentNodeId, isEnabled, mappingSessionStore, setCopiedAIResponse]);

  const handleDuplicateTable = useCallback(() => {
    if (!generatedDocument?.id || !editor1Ref.current) {
      notifyError('Unable to duplicate table.');
      return;
    }

    attachDuplicateTableUndoListeners();

    const anchorNodeId = currentTargetNodeIds[0];
    if (!anchorNodeId) {
      notifyError('Select a cell in the table you want to duplicate.');
      return;
    }

    if (isLockedByOtherUser(anchorNodeId)) {
      const lockerInfo = getNodeLocker(anchorNodeId);
      notifyError(
        lockerInfo
          ? `This node is being edited by ${lockerInfo.username}`
          : 'This node is locked by another user.',
      );
      return;
    }

    if (
      isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) &&
      mappingSessionStore?.isLocked(anchorNodeId)
    ) {
      notifyError('This node is locked by another user.');
      return;
    }

    const sourceTable = getTableForTargetNodeId(editor1Ref, anchorNodeId);
    if (!sourceTable) {
      notifyError('Selection is not inside a table.');
      return;
    }

    const duplicateUndoScroll = getTargetEditorWindowScroll(editor1Ref.current);

    const { oldToNewNodeIdMap, newRootTable } = duplicateTableInEditor(
      editor1Ref.current,
      sourceTable,
      String(generatedDocument.id),
    );
    lastDuplicateOldToNewMapRef.current = oldToNewNodeIdMap;
    duplicateUndoContextStackRef.current.push({
      anchorNodeId,
      oldToNewNodeIdMap,
      scroll: duplicateUndoScroll,
    });

    const copiedSuggestions = copyAiResponseForDuplicatedTable(
      oldToNewNodeIdMap,
      updatedAiResponse,
      (id) => getCurrentNodeId(id, editor1Ref, updatedAiResponse),
    );

    if (Object.keys(copiedSuggestions).length) {
      setUpdatedAiResponse((prev) => ({ ...prev, ...copiedSuggestions }));
      setCopiedAIResponse((prev) => ({ ...prev, ...copiedSuggestions }));
    }

    editor1Ref.current.setDirty(true);
    handleEditorChange();
    setTargetChangesStatus(TargetSaveState.UNSAVED);

    const firstNewCell = newRootTable.querySelector<HTMLElement>('td, th');
    const firstNewCellId = firstNewCell?.getAttribute('data-nodeid');
    if (firstNewCellId) {
      handleTargetNodeIds([firstNewCellId]);
      setColors([AUTHORING_COLORS.SELECTED]);

      if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && mappingSessionStore) {
        mappingSessionStore.setActiveNode('target', firstNewCellId);
        mappingSessionStore.setActiveContent('target', editor1Ref.current.getContent());
      }
    }

    highlightAllTargetSuggestions(editor1Ref);
    newRootTable.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    notifySuccess('Table duplicated. Press Ctrl+Z to undo.');
  }, [
    generatedDocument?.id,
    currentTargetNodeIds,
    editor1Ref,
    attachDuplicateTableUndoListeners,
    isLockedByOtherUser,
    getNodeLocker,
    isEnabled,
    mappingSessionStore,
    updatedAiResponse,
    getCurrentNodeId,
    setUpdatedAiResponse,
    setCopiedAIResponse,
    handleEditorChange,
    setTargetChangesStatus,
    handleTargetNodeIds,
    setColors,
    highlightAllTargetSuggestions,
  ]);

  useEffect(() => {
    const sourceAnchorNodeId = currentSourceNodeIds[0];
    if (sourceAnchorNodeId && getTableForSourceNodeId(editor2Ref, sourceAnchorNodeId)) {
      lastSourceTableAnchorRef.current = sourceAnchorNodeId;
    }
  }, [currentSourceNodeIds, editor2Ref]);

  const resolveSourceTableAnchorNodeId = useCallback((): string | null => {
    const candidates = [currentSourceNodeIds[0], lastSourceTableAnchorRef.current];
    return (
      candidates.find(
        (nodeId): nodeId is string => !!nodeId && !!getTableForSourceNodeId(editor2Ref, nodeId),
      ) ?? null
    );
  }, [currentSourceNodeIds, editor2Ref]);

  const isTransferTableEnabled = useMemo(() => {
    const sourceAnchorNodeId = resolveSourceTableAnchorNodeId();
    const targetAnchorNodeId = currentTargetNodeIds[0];
    if (!sourceAnchorNodeId || !targetAnchorNodeId) {
      return false;
    }

    if (isLockedByOtherUser(sourceAnchorNodeId) || isLockedByOtherUser(targetAnchorNodeId)) {
      return false;
    }

    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && mappingSessionStore) {
      if (
        mappingSessionStore.isLocked(sourceAnchorNodeId) ||
        mappingSessionStore.isLocked(targetAnchorNodeId)
      ) {
        return false;
      }
    }

    return !!getTargetTableInsertionHost(editor1Ref, targetAnchorNodeId);
  }, [
    resolveSourceTableAnchorNodeId,
    currentTargetNodeIds,
    editor1Ref,
    isLockedByOtherUser,
    isEnabled,
    mappingSessionStore,
  ]);

  const handleTransferTable = useCallback(() => {
    if (!generatedDocument?.id || !editor1Ref.current || !editor2Ref.current) {
      notifyError('Unable to transfer table.');
      return;
    }

    attachDuplicateTableUndoListeners();

    const sourceAnchorNodeId = resolveSourceTableAnchorNodeId();
    const targetAnchorNodeId = currentTargetNodeIds[0];
    if (!sourceAnchorNodeId || !targetAnchorNodeId) {
      notifyError('Select a cell in the source table and a target location to insert the table.');
      return;
    }

    if (isLockedByOtherUser(sourceAnchorNodeId) || isLockedByOtherUser(targetAnchorNodeId)) {
      notifyError('One of the selected nodes is locked by another user.');
      return;
    }

    if (
      isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) &&
      mappingSessionStore &&
      (mappingSessionStore.isLocked(sourceAnchorNodeId) ||
        mappingSessionStore.isLocked(targetAnchorNodeId))
    ) {
      notifyError('One of the selected nodes is locked by another user.');
      return;
    }

    const sourceTable = getTableForSourceNodeId(editor2Ref, sourceAnchorNodeId);
    if (!sourceTable) {
      notifyError('Source selection is not inside a table.');
      return;
    }

    const transferUndoScroll = getTargetEditorWindowScroll(editor1Ref.current);

    const transferResult = transferSourceTableInTargetEditor(
      editor1Ref.current,
      sourceTable,
      String(generatedDocument.id),
      targetAnchorNodeId,
      editor1Ref,
    );
    if (!transferResult) {
      notifyError('Unable to insert the table at the selected target location.');
      return;
    }

    const { sourceToTargetNodeIdMap, newRootTable } = transferResult;
    lastDuplicateOldToNewMapRef.current = sourceToTargetNodeIdMap;
    duplicateUndoContextStackRef.current.push({
      anchorNodeId: targetAnchorNodeId,
      oldToNewNodeIdMap: sourceToTargetNodeIdMap,
      scroll: transferUndoScroll,
    });

    const sourceFileIdValue =
      Number(getCatalogId(editor2Ref, sourceAnchorNodeId) ?? selectedCatalogId) ||
      selectedCatalogId;

    const aiPayload = buildAiResponseForTransferredTable(
      sourceToTargetNodeIdMap,
      sourceTable,
      sourceFileIdValue,
    );

    if (mappingType === MappingTypeEnum.AI_AUTHORING) {
      if (Object.keys(aiPayload).length) {
        setUpdatedAiResponse((prev) => ({ ...prev, ...aiPayload }));
        setCopiedAIResponse((prev) => ({ ...prev, ...aiPayload }));
      }
    } else {
      queuedTableTransferSuggestionsRef.current.push(
        ...buildAuthorTransferSuggestions(
          sourceToTargetNodeIdMap,
          sourceTable,
          sourceFileIdValue,
          generatedDocument.id,
          sessionId,
        ),
      );
    }

    editor1Ref.current.setDirty(true);
    handleEditorChange();
    setTargetChangesStatus(TargetSaveState.UNSAVED);

    const firstNewCell = newRootTable.querySelector<HTMLElement>('td, th');
    const firstNewCellId = firstNewCell?.getAttribute('data-nodeid');
    if (firstNewCellId) {
      handleTargetNodeIds([firstNewCellId]);
      setColors([AUTHORING_COLORS.SELECTED]);

      if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && mappingSessionStore) {
        mappingSessionStore.setActiveNode('target', firstNewCellId);
        mappingSessionStore.setActiveContent('target', editor1Ref.current.getContent());
      }
    }

    highlightAllTargetSuggestions(editor1Ref);
    newRootTable.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    notifySuccess('Table transferred from source. Save to register mappings.');
  }, [
    generatedDocument?.id,
    editor1Ref,
    editor2Ref,
    attachDuplicateTableUndoListeners,
    resolveSourceTableAnchorNodeId,
    currentTargetNodeIds,
    isLockedByOtherUser,
    isEnabled,
    mappingSessionStore,
    selectedCatalogId,
    mappingType,
    sessionId,
    setUpdatedAiResponse,
    setCopiedAIResponse,
    handleEditorChange,
    setTargetChangesStatus,
    handleTargetNodeIds,
    setColors,
    highlightAllTargetSuggestions,
  ]);

  useEffect(() => {
    if (lastChangeTime === null || !runAutoSave) return;

    // Clear existing autosave timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    if (isDocumentChanged()) {
      lockCurrentNodesRef.current();
      setTargetChangesStatus(TargetSaveState.UNSAVED);

      autosaveTimerRef.current = setTimeout(async () => {
        try {
          await handleSaveRef.current(TargetSaveState.AUTOSAVED, AcceptanceMethodEnum.AUTO_SAVE);
        } finally {
          setLastChangeTime(null);
          setIsUndoEnabled(false);
          editor1Ref.current?.undoManager.clear();
        }
      }, AUTOSAVE_DELAY);
    } else {
      setLastChangeTime(null);
      setTargetChangesStatus(TargetSaveState.UNCHANGED);
      editor1Ref.current?.undoManager.clear();
      setIsUndoEnabled(false);
    }
  }, [lastChangeTime, runAutoSave, editor1Ref, isDocumentChanged]);

  const handleAiDropDownChange = (option: SearchOptions) => {
    setSuggestionIndex((prev) => ({ ...prev, value: option.value, nodeId: option.nodeId }));
  };

  const setAllCells = () => {
    const body = editor1Ref.current?.getBody();
    if (body) setCells(getAllCells(body.outerHTML));
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'g') setIsCtrlPressed(false);
    else setIsCtrlPressed(event.ctrlKey || event.metaKey);
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (
      event.code === 'ControlLeft' ||
      event.code === 'ControlRight' ||
      event.code === 'MetaLeft' ||
      event.code === 'MetaRight'
    ) {
      setIsCtrlPressed(false);
    }
  };

  const handleLinkNodes = async () => {
    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && mappingSessionStore) {
      const targetIds = mappingSessionStore.activeNodeIds.get('target') ?? [];
      const sourceIds = mappingSessionStore.activeNodeIds.get('source') ?? [];
      const selectedContent = mappingSessionStore.getSelectedContent();
      const selectedRange = mappingSessionStore.getSelectedRange();

      if (targetIds.length !== 1 || sourceIds.length !== 1) {
        notifyError('Select one target region and one source region, then select text in each.');
        return;
      }
      if (!selectedContent || !selectedRange) {
        notifyError('Select text in both the target and source documents to link.');
        return;
      }

      const targetEls = mappingSessionStore.getElementsFromContent('target');
      const sourceEls = mappingSessionStore.getElementsFromContent('source');
      const sfId = mappingSessionStore.sourceFileId;
      if (!targetEls?.length || !sourceEls?.length || !sfId) {
        notifyError('Unable to link: missing editor content or source file.');
        return;
      }

      const targetSuggestions = targetIds.map((id) =>
        mappingSessionStore.getSuggestionByTargetId(id),
      );

      await handleNewSmartSuggestion(
        targetEls,
        sourceEls,
        String(sfId),
        AcceptanceMethodEnum.AUTHOR,
        targetSuggestions,
        selectedContent,
        selectedRange,
      );
      mappingSessionStore.clearTextSelection();
      return;
    }

    let targetValue;
    let sourceValue;
    const updatedSelection = linkTextSelectionNode();
    if (updatedSelection) {
      const { targetValue: targetVal, sourceValue: sourceVal } = updatedSelection;
      targetValue = targetVal;
      sourceValue = sourceVal;
    } else {
      await handleSaveChanges(undefined, AcceptanceMethodEnum.AUTHOR);
      targetValue = getElement(editor1Ref, currentTargetNodeIds[0])?.innerHTML;
    }
    if (currentTargetNodeIds.length === 1 && currentSourceNodeIds.length === 1) {
      const suggestion = {
        sessionId: sessionId,
        targetNodeId: currentTargetNodeIds[0],
        sourceNodeId: currentSourceNodeIds[0],
        documentId: getCatalogId(editor2Ref, currentSourceNodeIds[0]),
        sourceFileId: getCatalogId(editor2Ref, currentSourceNodeIds[0]),
        targetValue: targetValue,
        mapperType: 'author',
        ...(!!sourceValue && { sourceValue }),
      };

      callSaveSuggestions([suggestion], AcceptanceMethodEnum.AUTHOR, false);
    }
  };

  // Allow comments on any single selected target node (table cell or block with data-nodeid),
  // not only cells that appear in the suggestion map.
  const displayCommentButton = currentTargetNodeIds.length === 1 && !!currentTargetNodeIds[0];

  const currentSuggestionId = targetNodeIdSuggestionMap[currentTargetNodeIds[0]]
    ? targetNodeIdSuggestionMap[currentTargetNodeIds[0]].id
    : null;

  const { commentModal, openCommentModalWithThread } = useGeneralizationComments(
    currentSuggestionId ?? 0,
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const svgValues = useMemo(() => {
    const values: Record<string, Record<string, unknown>[]> = {};
    Object.entries(updatedAiResponse).forEach(([targetId, sourceNodes]) => {
      sourceNodes.forEach((targetNode: Record<string, Record<string, unknown>>) => {
        const [key, value] = Object.entries(targetNode)[0];
        const sourceNode = { ...value, targetId };

        if (values[key]) {
          values[key].push(sourceNode);
        } else {
          values[key] = [sourceNode];
        }
      });
    });
    return values;
  }, [updatedAiResponse]);

  const memoizedCatalogWithSourceIds = useMemo(() => {
    return getCatalogWithSourceIdsFromAIResponse(updatedAiResponse);
  }, [updatedAiResponse]);

  const highlightAllSourceDocumentSuggestions = useCallback(() => {
    if (selectedValue) {
      const nodeIds = memoizedCatalogWithSourceIds[selectedValue];
      applyCustomSVGs(svgValues as Record<string, MarkerAttributes[]>, editor2Ref);
      const color = '#D3D3D3';
      highlightSuggestions(editor2Ref, nodeIds, color);
    }
  }, [editor2Ref, highlightSuggestions, memoizedCatalogWithSourceIds, selectedValue, svgValues]);

  const handleAiResponseUpdate = useCallback(
    (
      snappingEnabled: boolean,
      shouldClearAISuggestions: boolean,
      aiResponseCopyData: AIResponse,
    ) => {
      if (snappingEnabled) {
        highlightAllSourceDocumentSuggestions();
      }

      if (shouldClearAISuggestions) {
        const tdNodeIds = aiResponseCopyData && getCellElementIds(aiResponseCopyData);
        tdNodeIds && removeHighlightSuggestions(editor1Ref, tdNodeIds);
      }

      highlightAllTargetSuggestions(editor1Ref);
    },
    [
      editor1Ref,
      getCellElementIds,
      highlightAllSourceDocumentSuggestions,
      highlightAllTargetSuggestions,
    ],
  );

  // Ref so the effect below always calls the latest version without being in its deps.
  // Including handleAiResponseUpdate directly caused an infinite loop: the effect sets
  // updatedAiResponse → svgValues/memoizedCatalogWithSourceIds recompute → handleAiResponseUpdate
  // gets a new ref → effect re-fires → new filteredAiResponse object → repeat.
  const handleAiResponseUpdateRef = useRef(handleAiResponseUpdate);
  useEffect(() => {
    handleAiResponseUpdateRef.current = handleAiResponseUpdate;
  }, [handleAiResponseUpdate]);

  const highlightAllTargetSuggestionsRef = useRef(highlightAllTargetSuggestions);
  useEffect(() => {
    highlightAllTargetSuggestionsRef.current = highlightAllTargetSuggestions;
  }, [highlightAllTargetSuggestions]);

  useEffect(() => {
    if (aiResponse && acceptedSuggestions) {
      const aiSuggestionWithId = lockSourceFile
        ? filteredAiResponse(newAiResponse, selectedCatalogId)
        : newAiResponse;
      setUpdatedAiResponse((prev) =>
        _.isEqual(prev, aiSuggestionWithId) ? prev : aiSuggestionWithId,
      );
      setLockedAiResponse((prev) => (_.isEqual(prev, newAiResponse) ? prev : newAiResponse));
      aiResponseCopy &&
        handleAiResponseUpdateRef.current(isSnappingEnabled, clearAISuggestions, aiResponseCopy);
    }
  }, [
    aiResponse,
    acceptedSuggestions,
    aiResponseCopy,
    clearAISuggestions,
    isSnappingEnabled,
    lockSourceFile,
    newAiResponse,
    selectedCatalogId,
  ]);

  useEffect(() => {
    if (lockSourceFile) {
      const filteredResponse = filteredAiResponse(lockedAiResponse, selectedCatalogId);
      setUpdatedAiResponse((prev) => (_.isEqual(prev, filteredResponse) ? prev : filteredResponse));
    } else {
      setUpdatedAiResponse((prev) => (_.isEqual(prev, lockedAiResponse) ? prev : lockedAiResponse));
    }
  }, [lockSourceFile, lockedAiResponse, selectedCatalogId]);

  const selectedNodesRatio = `${currentSourceNodeIds.length} / ${currentTargetNodeIds.length}`;

  const disableLinkButton = !(
    currentTargetNodeIds.length === 1 && currentSourceNodeIds.length === 1
  );

  const getSourceCatalogId = useCallback(() => {
    const sourceNodeIdValue = sourceNodeId()?.at(0);
    if (sourceNodeIdValue && sourceNodeIdValue === currentSourceNodeIds[0]) {
      return sourceFileId();
    } else if (aiResponse) {
      const matchingNode = aiResponse[currentTargetNodeIds[0]]?.find(
        (s) => Object.keys(s)[0] === currentSourceNodeIds[0],
      );

      if (matchingNode) {
        return matchingNode[currentSourceNodeIds[0]].sourceFileId;
      }
    }
  }, [aiResponse, currentSourceNodeIds, currentTargetNodeIds, sourceFileId, sourceNodeId]);

  useEffect(() => {
    if (!loadingGeneratedDocument) {
      const html = generatedDocument ? generatedDocument.html : '';
      setEditor1Content((prev) => (prev.html === html ? prev : { html }));
    }
    if (!clearAISuggestions) {
      highlightAllTargetSuggestionsRef.current(editor1Ref);
    }
  }, [
    clearAISuggestions,
    editor1Ref,
    generatedDocument,
    loadingGeneratedDocument,
    setEditor1Content,
  ]);

  useEffect(() => {
    if (!loadingGeneratedDocument && !loadingSourceDocument) {
      setIsLoading(false);
    }
  }, [loadingGeneratedDocument, loadingSourceDocument]);

  useEffect(() => {
    userPickedSourceRef.current = false;
  }, [currentTargetNodeIds]);

  useEffect(() => {
    if (userPickedSourceRef.current) return;
    const sourceNodeIds = suggestionIndex.nodeId ? [suggestionIndex.nodeId] : sourceNodeId();
    if (sourceNodeIds && sourceNodeIds.length > 0) {
      setCurrentSourceNodeIds((previousSourceNodeIds) => {
        setPreviousSourceNodeIds(previousSourceNodeIds);
        return sourceNodeIds ?? [];
      });
      setIsDisabledApprove(true);
    }
  }, [suggestionIndex, sourceNodeId]);

  useEffect(() => {
    if (currentSourceNodeIds.length === 1) {
      const catId = isSearched
        ? searchResult.length &&
          searchResult.find((result) => result.dataNodeId === suggestionIndex.nodeId)?.catalog_id
        : getSourceCatalogId();
      if (catId && (selectedCatalogId !== +catId || +selectedValue !== +catId)) {
        handleSourcechange(catId?.toString());
      }
    }
    previousSourceNodeIds.forEach((previousSourceNodeId) => {
      const element = getElement(editor2Ref, previousSourceNodeId);
      const allNodesIds = getAllNodesIdsInElement(element);
      const needHighlighting =
        memoizedCatalogWithSourceIds[selectedCatalogId] &&
        allNodesIds.some(
          (nodeId) => memoizedCatalogWithSourceIds[selectedCatalogId].indexOf(nodeId) !== -1,
        );
      element && highlightPreviousNode(editor2Ref, needHighlighting, element);
    });
    const scrollToSource = !userPickedSourceRef.current;
    currentSourceNodeIds.forEach((currentSourceNodeId, index) => {
      const element = getElement(editor2Ref, currentSourceNodeId);
      highlightCurrentNode(editor2Ref, colors[index], element, scrollToSource && index === 0);
    });

    highlightMatchingElements(editor1Ref, editor2Ref, currentTargetNodeIds);
    (editor2Ref.current?.selection as { clearDrag?: () => void } | undefined)?.clearDrag?.();
  }, [
    colors,
    currentSourceNodeIds,
    currentTargetNodeIds,
    editor1Ref,
    editor2Ref,
    getSourceCatalogId,
    handleSourcechange,
    highlightCurrentNode,
    highlightPreviousNode,
    isSearched,
    memoizedCatalogWithSourceIds,
    previousSourceNodeIds,
    searchResult,
    selectedCatalogId,
    selectedValue,
    suggestionIndex.nodeId,
  ]);

  useEffect(() => {
    if (!_.isEmpty(currentSourceNodeIds)) {
      const scrollToSource = !userPickedSourceRef.current;
      currentSourceNodeIds.forEach((currentSourceNodeId, index) =>
        highlightCurrentNode(
          editor2Ref,
          colors[index],
          getElement(editor2Ref, currentSourceNodeId),
          scrollToSource && index === 0,
        ),
      );
      highlightMatchingElements(editor1Ref, editor2Ref, currentTargetNodeIds);
    }
  }, [
    colors,
    currentSourceNodeIds,
    currentTargetNodeIds,
    editor1Ref,
    editor2Ref,
    highlightCurrentNode,
    sourceFileDoc,
  ]);

  useEffect(() => {
    !isNextAuthoringCellAvailable && setIsDisabledApprove(true);

    if (currentTargetNodeIds.length === 1) {
      const currentElement = getElement(editor1Ref, currentTargetNodeIds[0]) as HTMLElement;
      const cellElement = getCellElement(currentElement, editor1Ref?.current?.getBody() as Node);
      if (
        cellElement?.tagName === 'TH' ||
        (cellElement?.tagName === 'TD' && cellElement?.cellIndex === 0)
      ) {
        setIsHeaderCellSelected(true);
      } else {
        setIsHeaderCellSelected(false);
      }
      setSuggestionIndex((prev: { value: number }) => ({
        ...prev,
        value: getSourceNodeIndex(currentTargetNodeIds[0], updatedAiResponse),
        nodeId: '',
      }));

      if (updatedAiResponse) {
        const resolvedNodeId = getCurrentNodeId(currentTargetNodeIds[0]);
        setAiSuggestionList(updatedAiResponse[resolvedNodeId]);
      }
    } else if (currentTargetNodeIds.length > 1) {
      setIsHeaderCellSelected(true);
    } else {
      setIsHeaderCellSelected(false);
    }

    previousTargetNodeIds.forEach((previousTargetNodeId) => {
      const element = getElement(editor1Ref, previousTargetNodeId);
      highlightPreviousNode(editor1Ref, false, element);
    });
    highlightCurrentNodes();
  }, [
    currentTargetNodeIds,
    editor1Ref,
    getCurrentNodeId,
    getSourceNodeIndex,
    highlightCurrentNodes,
    highlightPreviousNode,
    isNextAuthoringCellAvailable,
    previousTargetNodeIds,
    updatedAiResponse,
  ]);

  useEffect(() => {
    const isDisabled =
      _.isEmpty(currentTargetNodeIds) ||
      _.isEmpty(currentSourceNodeIds) ||
      currentTargetNodeIds.length !== currentSourceNodeIds.length ||
      !!sourceSelection !== !!targetSelection;

    setIsDisabledApprove(isDisabled);
  }, [currentTargetNodeIds, currentSourceNodeIds, sourceSelection, targetSelection]);

  useEffect(() => {
    if (sessionId) {
      fetchMappingSessionComments(sessionId)
        .then((res) => {
          setMappingComments([...res.data]);
        })
        .catch((err) => console.error(err));
    }
  }, [sessionId, setMappingComments]);

  useEffect(() => {
    if (!mappingComments?.length) {
      return;
    }
    const id = requestAnimationFrame(() => {
      addCommentBubbles();
    });
    return () => cancelAnimationFrame(id);
  }, [mappingComments, addCommentBubbles]);

  return {
    isLoading,
    isDisable,
    isDisabledApprove,
    setIsDisable,
    highlightAllTargetSuggestions,
    highlightAllSourceDocumentSuggestions,
    receiveSuggestions,
    autofillSuggestions,
    isUndoAvailable,
    isRedoAvailable,
    handleGeneralizationAuthoringUndo,
    handleGeneralizationAuthoringRedo,
    targetOnClickHandler,
    sourceDocumentOnClickHandler,
    aiSuggestionList,
    handleAiDropDownChange,
    setCurrentTargetNodeIds,
    setPreviousTargetNodeIds,
    handleSave,
    isSaving,
    handleKeyDown,
    handleKeyUp,
    selectedNodesRatio,
    setSearchResult,
    setIsSearched,
    suggestionNodeValues,
    disableLinkButton,
    handleLinkNodes,
    isImageTagging,
    toggleImageTagging,
    suggestionsWithColors,
    updatedAiResponse,
    setUpdatedAiResponse,
    isNextAuthoringCellAvailable,
    isPreviousAuthoringCellAvailable,
    navigateToAuthoringCell,
    getCurrentNodeId,
    handleReset,
    lockedAiResponse,
    getCellElementIds,
    handleLockedFileChange,
    handleSnappingToggle,
    isSnappingEnabled,
    setCopiedAIResponse,
    copiedAIResponse,
    handleTargetNodeIds,
    currentTargetNodeIds,
    isSavingSuggestions,
    isHeaderCellSelected,
    callSaveSuggestions,
    setColors,
    isTaggingSlideOverOpen,
    handleClosePinningSlideOver,
    handleSubmitTag,
    imageTagFormValues,
    targetChangesStatus,
    setTargetChangesStatus,
    openCommentModalWithThread,
    commentModal,
    displayCommentButton,
    isNewSuggestionsActive,
    setIsNewSuggestionsActive,
    autoFillHandler,
    isImageNodeSelected,
    handleSourceNodeIds,
    isUndoEnabled,
    handleEditorChange,
    lockedNodes,
    activeUsers,
    handleNewSmartSuggestion,
    baseTargetFileId,
    handleDuplicateTable,
    isDuplicateTableEnabled,
    handleTransferTable,
    isTransferTableEnabled,
    hasPendingAuthoringSave,
    setIsAutofill,
  };
};
